import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressMembersCustomfieldsCustomFieldsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/members/v1/custom-fields',
        destPath: '/v1/custom-fields',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/members/v1/custom-fields',
        destPath: '/v1/custom-fields',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/members/v1/custom-fields',
        destPath: '/v1/custom-fields',
      },
    ],
    'editor-flow.wixapps.net': [
      {
        srcPath: '/_api/members/v1/custom-fields',
        destPath: '/v1/custom-fields',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/members/v1/custom-fields',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/members/v1/custom-fields',
        destPath: '/v1/custom-fields',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/members/v1/custom-fields',
        destPath: '/v1/custom-fields',
      },
    ],
    _: [
      {
        srcPath: '/_api/members/v1/custom-fields',
        destPath: '/v1/custom-fields',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_members_custom-fields';

/** Creates a custom field. */
export function createCustomField(payload: object): RequestOptionsFactory<any> {
  function __createCustomField({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'field.createdDate' }, { path: 'field.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.members.customfields.CustomFields.CreateCustomField',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsCustomFieldsUrl({
        protoPath: '/v1/custom-fields',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'field.createdDate' },
              { path: 'field.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createCustomField;
}

/** Retrieves a custom field by ID. */
export function getCustomField(payload: object): RequestOptionsFactory<any> {
  function __getCustomField({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.members.customfields.CustomFields.GetCustomField',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsCustomFieldsUrl({
        protoPath: '/v1/custom-fields/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'field.createdDate' },
              { path: 'field.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getCustomField;
}

/**
 * Retrieves a list of custom fields and fields provided by Members Area, given the provided paging.
 *
 * The fields are ordered by section in the following sequence:
 * - `GENERAL`
 * - `DISPLAY_INFO`
 * - `SOCIAL`
 *
 * The fields in the same section are ordered by the field creation date.
 *
 * To change the order of fields in sections, call Update Custom Fields Order.
 */
export function listCustomFields(payload: object): RequestOptionsFactory<any> {
  function __listCustomFields({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.members.customfields.CustomFields.ListCustomFields',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsCustomFieldsUrl({
        protoPath: '/v1/custom-fields',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'fields.createdDate' },
              { path: 'fields.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listCustomFields;
}

/**
 * Updates a custom field.
 *
 * For custom fields you can update the following fields: `name`, `defaultPrivacy`, and `socialType`.
 *
 * For system fields you can only update the `defaultPrivacy` field.
 */
export function updateCustomField(payload: object): RequestOptionsFactory<any> {
  function __updateCustomField({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'field.createdDate' }, { path: 'field.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.members.customfields.CustomFields.UpdateCustomField',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsCustomFieldsUrl({
        protoPath: '/v1/custom-fields/{field.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'field.createdDate' },
              { path: 'field.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateCustomField;
}

/**
 * Deletes a custom field and removes the corresponding column from the [Contact List](https://support.wix.com/en/article/wix-contacts-managing-your-contact-list)
 * display. The custom field is no longer suggested when adding custom fields.
 */
export function deleteCustomField(payload: object): RequestOptionsFactory<any> {
  function __deleteCustomField({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.members.customfields.CustomFields.DeleteCustomField',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsCustomFieldsUrl({
        protoPath: '/v1/custom-fields/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteCustomField;
}

/**
 * Hides a custom field from the custom fields list, yet it's still available as a [suggested field](https://dev.wix.com/docs/rest/crm/members-contacts/members/custom-fields/custom-field-suggestions/introduction)
 * to add later.
 *
 * The related contacts custom field will still be available in the [Contact List](https://support.wix.com/en/article/wix-contacts-managing-your-contact-list).
 */
export function hideCustomField(payload: object): RequestOptionsFactory<any> {
  function __hideCustomField({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.members.customfields.CustomFields.HideCustomField',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsCustomFieldsUrl({
        protoPath: '/v1/custom-fields/{id}/hide',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __hideCustomField;
}

/** Updates the order of custom fields in a section. */
export function updateCustomFieldsOrder(
  payload: object
): RequestOptionsFactory<any> {
  function __updateCustomFieldsOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.members.customfields.CustomFields.UpdateCustomFieldsOrder',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsCustomFieldsUrl({
        protoPath: '/v1/custom-fields/order',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'fields.createdDate' },
              { path: 'fields.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateCustomFieldsOrder;
}
